import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { CalendarClock, Loader2, Send, Tag, Users } from 'lucide-react';
import { getEmailApiBaseUrl } from '../../../utils/emailApi';
import { getEmailTagDisplayName } from '../../../utils/emailTagLabel';
import LoadingSpinner from '../../common/LoadingSpinner';
import {
  gymMarketingCardInner,
  gymMarketingMainCol,
  gymMarketingSideCol,
  gymMarketingSplitGrid,
  gymModalInput,
  gymModalLabel,
  gymSocialBtnPrimary,
  gymSocialBtnSecondary,
  gymSocialCard,
  gymSocialMutedText,
  gymSocialTitleText,
} from '../gymPageTheme';
import GymEmailCampaignEditor from './GymEmailCampaignEditor';
import GymEmailCampaignHistory from './GymEmailCampaignHistory';

function isEmailBodyEmpty(html) {
  if (!html || !String(html).trim()) {
    return true;
  }
  const text = String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text === '';
}

const POLL_INTERVAL_MS = 15000;
const SCHEDULED_POLL_MARGIN_MS = 10 * 60 * 1000;
const SCHEDULED_POLL_LOOKAHEAD_MS = 2 * 60 * 1000;
const POST_SEND_POLL_MS = 15 * 60 * 1000;

function parseCampaignWhen(raw) {
  if (!raw) {
    return null;
  }
  const ms = new Date(String(raw).replace(' ', 'T')).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function campaignNeedsPolling(campaign) {
  const now = Date.now();

  if (campaign.status === 'sending') {
    return true;
  }

  // Serie ricorrente: continua a pollare dopo l'invio finché last_run_at è recente
  if (campaign.recurrence_enabled && campaign.last_run_at) {
    const lastMs = parseCampaignWhen(campaign.last_run_at);
    if (lastMs != null && now < lastMs + POST_SEND_POLL_MS) {
      return true;
    }
  }

  if (campaign.status !== 'scheduled') {
    return false;
  }

  const whenMs = parseCampaignWhen(campaign.next_run_at || campaign.scheduled_at);
  if (whenMs == null) {
    return true;
  }

  // Finestra attorno al prossimo invio programmato
  if (now >= whenMs - SCHEDULED_POLL_LOOKAHEAD_MS && now < whenMs + SCHEDULED_POLL_MARGIN_MS) {
    return true;
  }

  // Ancora scheduled ma orario passato — in attesa del cron
  if (now >= whenMs && now < whenMs + POST_SEND_POLL_MS) {
    return true;
  }

  return false;
}

function campaignsSignature(list) {
  return JSON.stringify(
    (list || []).map((c) => [
      c.id,
      c.status,
      c.scheduled_at,
      c.next_run_at,
      c.last_run_at,
      c.sent_at,
      c.recurrence_occurrence_count,
      c.success_count,
      c.recurrence_paused,
    ])
  );
}

export default function GymEmailCampaignsPanel({ api, templateDraft, onTemplateDraftConsumed }) {
  const { t } = useTranslation();
  const emailApi = getEmailApiBaseUrl();

  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [weeklyLimit, setWeeklyLimit] = useState(10);
  const [weeklyUsed, setWeeklyUsed] = useState(0);

  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [recurrenceEnabled, setRecurrenceEnabled] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState('weekly');
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);
  const [recurrenceMaxOccurrences, setRecurrenceMaxOccurrences] = useState('');
  const [recurrencePreview, setRecurrencePreview] = useState([]);
  const [recurrencePreviewLoading, setRecurrencePreviewLoading] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewMembers, setPreviewMembers] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const campaignsSigRef = useRef('');
  const expandedIdRef = useRef(null);

  useEffect(() => {
    expandedIdRef.current = expandedId;
  }, [expandedId]);

  useEffect(() => {
    if (!templateDraft) {
      return;
    }
    if (templateDraft.subject) {
      setSubject(templateDraft.subject);
    }
    if (templateDraft.bodyHtml) {
      setBodyHtml(templateDraft.bodyHtml);
    }
    onTemplateDraftConsumed?.();
  }, [templateDraft, onTemplateDraftConsumed]);

  const loadCampaignDetail = useCallback(
    async (id, { silent = false } = {}) => {
      if (!silent) {
        setDetailLoading(true);
      }
      try {
        const res = await api.get(`gym_email_campaigns.php?action=get&id=${id}`, { baseURL: emailApi });
        if (expandedIdRef.current === id) {
          setDetail(res.data);
        }
      } catch (err) {
        if (!silent) {
          toast.error(err.response?.data?.error || t('gym.email.campaigns.loadError'));
          setExpandedId(null);
        }
      } finally {
        if (!silent) {
          setDetailLoading(false);
        }
      }
    },
    [api, emailApi, t]
  );

  const loadCampaigns = useCallback(
    async (options = {}) => {
      const { silent = false } = options;
      try {
        const campRes = await api.get('gym_email_campaigns.php?action=list', { baseURL: emailApi });
        const next = campRes.data?.campaigns || [];
        const sig = campaignsSignature(next);
        const changed = silent && sig !== campaignsSigRef.current;
        campaignsSigRef.current = sig;
        setCampaigns(next);
        setWeeklyLimit(campRes.data?.weekly_limit ?? 10);
        setWeeklyUsed(campRes.data?.weekly_used ?? 0);
        if (changed && expandedIdRef.current) {
          loadCampaignDetail(expandedIdRef.current, { silent: true });
        }
        return changed;
      } catch (err) {
        if (!silent) {
          toast.error(err.response?.data?.error || t('gym.email.campaigns.loadError'));
        }
        return false;
      }
    },
    [api, emailApi, t, loadCampaignDetail]
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const tagsRes = await api.get('gym_email_tags.php?action=list', { baseURL: emailApi });
      setTags(tagsRes.data?.tags || []);
      await loadCampaigns({ silent: false });
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.campaigns.loadError'));
    } finally {
      setLoading(false);
    }
  }, [api, emailApi, t, loadCampaigns]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!campaigns.some(campaignNeedsPolling)) {
      return undefined;
    }
    const poll = () => loadCampaigns({ silent: true });
    poll();
    const id = window.setInterval(poll, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [campaigns, loadCampaigns]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && campaigns.some(campaignNeedsPolling)) {
        loadCampaigns({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [campaigns, loadCampaigns]);

  const toggleTag = (tagId) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const runPreview = useCallback(
    async (tagIds) => {
      if (!tagIds?.length) {
        setPreviewCount(null);
        setPreviewMembers([]);
        return;
      }
      setPreviewLoading(true);
      try {
        const res = await api.post(
          'gym_email_campaigns.php?action=preview',
          { tag_ids: tagIds },
          { baseURL: emailApi }
        );
        setPreviewCount(res.data?.count ?? 0);
        setPreviewMembers(res.data?.members || []);
      } catch (err) {
        toast.error(err.response?.data?.error || t('gym.email.campaigns.previewError'));
        setPreviewCount(null);
        setPreviewMembers([]);
      } finally {
        setPreviewLoading(false);
      }
    },
    [api, emailApi, t]
  );

  useEffect(() => {
    runPreview(selectedTagIds);
  }, [selectedTagIds, runPreview]);

  useEffect(() => {
    if (!recurrenceEnabled || !scheduledAt) {
      setRecurrencePreview([]);
      return;
    }
    let cancelled = false;
    const loadPreview = async () => {
      setRecurrencePreviewLoading(true);
      try {
        const res = await api.post(
          'gym_email_campaigns.php?action=preview_recurrence',
          {
            scheduled_at: scheduledAt,
            recurrence_rule: { type: recurrenceType, interval: recurrenceInterval },
            count: 5,
          },
          { baseURL: emailApi }
        );
        if (!cancelled) {
          setRecurrencePreview(res.data?.dates || []);
        }
      } catch {
        if (!cancelled) {
          setRecurrencePreview([]);
        }
      } finally {
        if (!cancelled) {
          setRecurrencePreviewLoading(false);
        }
      }
    };
    const timer = setTimeout(loadPreview, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [api, emailApi, recurrenceEnabled, scheduledAt, recurrenceType, recurrenceInterval]);

  const validateForm = () => {
    if (selectedTagIds.length === 0) {
      toast.error(t('gym.email.campaigns.selectTag'));
      return false;
    }
    if (!subject.trim()) {
      toast.error(t('gym.email.campaigns.subjectRequired'));
      return false;
    }
    if (isEmailBodyEmpty(bodyHtml)) {
      toast.error(t('gym.email.campaigns.bodyRequired'));
      return false;
    }
    if (previewCount === 0) {
      toast.error(t('gym.email.campaigns.noRecipients'));
      return false;
    }
    return true;
  };

  const resetComposer = () => {
    setSubject('');
    setBodyHtml('');
    setSelectedTagIds([]);
    setScheduledAt('');
    setRecurrenceEnabled(false);
    setRecurrenceType('weekly');
    setRecurrenceInterval(1);
    setRecurrenceMaxOccurrences('');
    setRecurrencePreview([]);
    setPreviewCount(null);
    setPreviewMembers([]);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    if (weeklyUsed >= weeklyLimit) {
      toast.error(t('gym.email.campaigns.weeklyLimit', { max: weeklyLimit }));
      return;
    }
    if (!window.confirm(t('gym.email.campaigns.confirmSend', { count: previewCount }))) {
      return;
    }

    setSending(true);
    try {
      const res = await api.post(
        'gym_email_campaigns.php?action=send',
        {
          tag_ids: selectedTagIds,
          subject: subject.trim(),
          body_html: bodyHtml.trim(),
        },
        { baseURL: emailApi }
      );
      toast.success(
        t('gym.email.campaigns.sent', {
          sent: res.data?.sent ?? 0,
          failed: res.data?.failed ?? 0,
        })
      );
      resetComposer();
      loadCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.campaigns.sendError'));
    } finally {
      setSending(false);
    }
  };

  const handleSchedule = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    if (!scheduledAt) {
      toast.error(t('gym.email.campaigns.scheduleRequired'));
      return;
    }
    if (!window.confirm(
      recurrenceEnabled
        ? t('gym.email.campaigns.confirmScheduleRecurring', { count: previewCount })
        : t('gym.email.campaigns.confirmSchedule', { count: previewCount })
    )) {
      return;
    }

    setScheduling(true);
    try {
      const payload = {
        tag_ids: selectedTagIds,
        subject: subject.trim(),
        body_html: bodyHtml.trim(),
        scheduled_at: scheduledAt,
        recurrence_enabled: recurrenceEnabled,
      };
      if (recurrenceEnabled) {
        payload.recurrence_rule = { type: recurrenceType, interval: recurrenceInterval };
        const maxOcc = parseInt(recurrenceMaxOccurrences, 10);
        if (maxOcc > 0) {
          payload.recurrence_max_occurrences = maxOcc;
        }
      }
      await api.post(
        'gym_email_campaigns.php?action=schedule',
        payload,
        { baseURL: emailApi }
      );
      toast.success(t('gym.email.campaigns.scheduled'));
      resetComposer();
      loadCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.campaigns.scheduleError'));
    } finally {
      setScheduling(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm(t('gym.email.campaigns.cancelConfirm'))) {
      return;
    }
    try {
      await api.post('gym_email_campaigns.php?action=cancel', { id }, { baseURL: emailApi });
      toast.success(t('gym.email.campaigns.cancelled'));
      loadCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.campaigns.cancelError'));
    }
  };

  const handlePause = async (id) => {
    try {
      await api.post('gym_email_campaigns.php?action=pause', { id }, { baseURL: emailApi });
      toast.success(t('gym.email.campaigns.pausedRecurrence'));
      loadCampaigns();
      if (expandedId === id) {
        toggleDetail(id);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.campaigns.pauseError'));
    }
  };

  const handleResume = async (id) => {
    try {
      await api.post('gym_email_campaigns.php?action=resume', { id }, { baseURL: emailApi });
      toast.success(t('gym.email.campaigns.resumedRecurrence'));
      loadCampaigns();
      if (expandedId === id) {
        toggleDetail(id);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.campaigns.resumeError'));
    }
  };

  const toggleDetail = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(id);
    await loadCampaignDetail(id);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  const limitReached = weeklyUsed >= weeklyLimit;
  const busy = sending || scheduling;

  return (
    <div className={gymMarketingSplitGrid}>
      <div className={gymMarketingMainCol}>
        <div className={`${gymSocialCard} ${gymMarketingCardInner} space-y-5 w-full`}>
          <div>
            <h2 className={`text-lg font-semibold ${gymSocialTitleText}`}>{t('gym.email.campaigns.newTitle')}</h2>
            <p className={`text-sm mt-2 leading-relaxed ${gymSocialMutedText}`}>
              {t('gym.email.campaigns.newHintMulti')}
            </p>
            <p
              className={`text-xs mt-3 inline-flex rounded-lg px-3 py-1.5 border border-gray-200/80 dark:border-white/10 bg-gray-50 dark:bg-gray-800/50 ${gymSocialMutedText}`}
            >
              {t('gym.email.campaigns.weeklyQuota', { used: weeklyUsed, max: weeklyLimit })}
            </p>
          </div>

          <form className="space-y-5" onSubmit={handleSend}>
            <div className="rounded-lg border border-gray-200/90 dark:border-white/10 bg-gray-50/80 dark:bg-gray-800/30 p-4 sm:p-5">
              <span className={gymModalLabel}>{t('gym.email.campaigns.tags')}</span>
              <p className={`text-xs mt-1.5 mb-3 leading-relaxed ${gymSocialMutedText}`}>
                {t('gym.email.campaigns.tagsHint')}
              </p>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => {
                  const active = selectedTagIds.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      disabled={busy || limitReached}
                      onClick={() => toggleTag(tag.id)}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors ${
                        active
                          ? 'border-indigo-400 bg-indigo-100 text-indigo-900 shadow-sm dark:border-[#cba5fa]/50 dark:bg-indigo-900/50 dark:text-indigo-100'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 dark:border-white/20 dark:bg-gray-900/80 dark:text-white/85 dark:hover:bg-gray-700/40'
                      }`}
                    >
                      <Tag className="h-3.5 w-3.5 opacity-70" />
                      {getEmailTagDisplayName(tag, t)}
                    </button>
                  );
                })}
              </div>

              {selectedTagIds.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 text-sm mt-4 pt-3 border-t border-gray-200/70 dark:border-white/10">
                  <Users className="h-4 w-4 opacity-70" aria-hidden />
                  {previewLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <span className={gymSocialMutedText}>
                      {t('gym.email.campaigns.recipientPreview', { count: previewCount ?? 0 })}
                    </span>
                  )}
                </div>
              )}

              {previewMembers.length > 0 && (
                <ul
                  className={`text-xs mt-3 max-h-28 overflow-y-auto rounded-md border border-gray-200 dark:border-white/10 p-3 space-y-1 ${gymSocialMutedText}`}
                >
                  {previewMembers.map((m) => (
                    <li key={m.id}>
                      {m.name} — {m.email}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <label className="block">
              <span className={gymModalLabel}>{t('gym.email.campaigns.subject')}</span>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={255}
                className={`mt-2 w-full ${gymModalInput}`}
                placeholder={t('gym.email.campaigns.subjectPlaceholder')}
                disabled={busy || limitReached}
              />
            </label>

            <GymEmailCampaignEditor value={bodyHtml} onChange={setBodyHtml} disabled={busy || limitReached} />

            <label className="block">
              <span className={gymModalLabel}>{t('gym.email.campaigns.scheduleAt')}</span>
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                className={`mt-2 w-full max-w-sm ${gymModalInput}`}
                disabled={busy || limitReached}
              />
            </label>

            <div className="rounded-lg border border-gray-200/90 dark:border-white/10 bg-gray-50/80 dark:bg-gray-800/30 p-4 space-y-3">
              <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-white/90">
                <input
                  type="checkbox"
                  checked={recurrenceEnabled}
                  onChange={(e) => setRecurrenceEnabled(e.target.checked)}
                  disabled={busy || limitReached || !scheduledAt}
                />
                {t('gym.email.campaigns.recurrenceEnable')}
              </label>
              <p className={`text-xs ${gymSocialMutedText}`}>{t('gym.email.campaigns.recurrenceHint')}</p>

              {recurrenceEnabled && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className={gymModalLabel}>{t('gym.email.campaigns.recurrenceType')}</span>
                    <select
                      className={`mt-2 w-full ${gymModalInput}`}
                      value={recurrenceType}
                      onChange={(e) => setRecurrenceType(e.target.value)}
                      disabled={busy || limitReached}
                    >
                      <option value="weekly">{t('gym.email.campaigns.recurrenceWeekly')}</option>
                      <option value="monthly">{t('gym.email.campaigns.recurrenceMonthly')}</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className={gymModalLabel}>{t('gym.email.campaigns.recurrenceInterval')}</span>
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        type="number"
                        min={1}
                        max={52}
                        value={recurrenceInterval}
                        onChange={(e) => setRecurrenceInterval(Math.max(1, parseInt(e.target.value, 10) || 1))}
                        className={`w-20 ${gymModalInput}`}
                        disabled={busy || limitReached}
                      />
                      <span className={`text-sm ${gymSocialMutedText}`}>
                        {recurrenceType === 'monthly'
                          ? t('gym.email.campaigns.recurrenceIntervalMonths')
                          : t('gym.email.campaigns.recurrenceIntervalWeeks')}
                      </span>
                    </div>
                  </label>
                  <label className="block sm:col-span-2">
                    <span className={gymModalLabel}>{t('gym.email.campaigns.recurrenceMaxOccurrences')}</span>
                    <input
                      type="number"
                      min={1}
                      value={recurrenceMaxOccurrences}
                      onChange={(e) => setRecurrenceMaxOccurrences(e.target.value)}
                      className={`mt-2 w-full max-w-xs ${gymModalInput}`}
                      disabled={busy || limitReached}
                      placeholder="∞"
                    />
                  </label>
                  {(recurrencePreviewLoading || recurrencePreview.length > 0) && (
                    <div className="sm:col-span-2">
                      <p className={`text-xs font-medium mb-1 ${gymSocialMutedText}`}>
                        {t('gym.email.campaigns.recurrencePreviewTitle')}
                      </p>
                      {recurrencePreviewLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ul className={`text-xs space-y-0.5 ${gymSocialMutedText}`}>
                          {recurrencePreview.map((d) => (
                            <li key={d}>
                              {new Date(String(d).replace(' ', 'T')).toLocaleString('it-IT', {
                                day: '2-digit',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-3 pt-1">
              <button
                type="submit"
                disabled={busy || limitReached || previewCount === 0}
                className={`${gymSocialBtnPrimary} inline-flex items-center gap-2`}
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {t('gym.email.campaigns.sendNow')}
              </button>
              <button
                type="button"
                disabled={busy || limitReached || previewCount === 0 || !scheduledAt}
                onClick={handleSchedule}
                className={`${gymSocialBtnSecondary} inline-flex items-center gap-2`}
              >
                {scheduling ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
                {recurrenceEnabled
                  ? t('gym.email.campaigns.scheduleRecurring')
                  : t('gym.email.campaigns.schedule')}
              </button>
            </div>
            {limitReached && (
              <p className="text-sm text-amber-700 dark:text-amber-200">{t('gym.email.campaigns.weeklyLimit', { max: weeklyLimit })}</p>
            )}
          </form>
        </div>
      </div>

      <div className={gymMarketingSideCol}>
        <div className={`${gymSocialCard} ${gymMarketingCardInner} flex flex-col min-h-[320px] xl:min-h-0 xl:h-full`}>
          <h3 className={`text-sm font-semibold ${gymSocialTitleText}`}>{t('gym.email.campaigns.historyTitle')}</h3>
          <p className={`text-xs mt-1.5 mb-4 leading-relaxed ${gymSocialMutedText}`}>
            {t('gym.email.campaigns.historyHint')}
          </p>
          <GymEmailCampaignHistory
            campaigns={campaigns}
            expandedId={expandedId}
            detail={detail}
            detailLoading={detailLoading}
            onToggleDetail={toggleDetail}
            onCancel={handleCancel}
            onPause={handlePause}
            onResume={handleResume}
          />
        </div>
      </div>
    </div>
  );
}
